import React, { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import CategoryCard from "../CategoryCard";
import SinglePropSelectModal from "../SinglePropSelectModal";
import MultiPropSelectModal from "../MultiPropSelectModal";
import { BadgeGroup, ImpactText, ModalHeader } from "../Styles/InputStyles";

const DuesSettingsModal = ({
    onClose,
    settingsData,
    editSettingsMutate,
    isLoading,
    property,
    propertyList,
    fetchSettingsLoading,
    isDataLoaded,
    propertyParam,
    fetchDuesSettingsMutate,
    editSettingsLoading,
    selectedProperties,
    setSelectedProperties
}) => {
    const [settings, setSettings] = useState({
        hideDues: false,
        autoReminders: false,
        allowPartialPayment: false,
        manualReminders: false,
        expenseAllocationByBed: false,
    });

    const [filteredProperties, setFilteredProperties] = useState([]);
    const [showPropertyModal, setShowPropertyModal] = useState(false);
    const [showMultiSelectModal, setShowMultiSelectModal] = useState(false);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [selectedPropertyId, setSelectedPropertyId] = useState(null);
    // const [selectedProperties, setSelectedProperties] = useState([]);
    const [isPropertyChanging, setIsPropertyChanging] = useState(false);
    const allProperties = propertyList || [];

    useEffect(() => {
        if (propertyParam && filteredProperties.length > 0) {
            const propertyFromParam = filteredProperties.find(prop => prop.id === propertyParam);
            if (propertyFromParam && propertyFromParam.id !== selectedPropertyId) {
                setSelectedProperty(propertyFromParam);
                setSelectedPropertyId(propertyFromParam.id);
            }
        }
    }, [propertyParam, filteredProperties]);

    useEffect(() => {
        if (propertyList) {
            const filtered = propertyList?.filter(prop => prop.id !== 0) || [];
            setFilteredProperties(filtered);

            if (filtered.length > 0 && !selectedProperty) {
                let initialProperty;

                if (propertyParam && propertyParam !== 0) {
                    initialProperty = filtered.find(prop => prop.id === propertyParam);
                }

                if (!initialProperty) {
                    initialProperty = filtered[0];
                }

                setSelectedProperty(initialProperty);
                setSelectedPropertyId(initialProperty.id);

                if (fetchDuesSettingsMutate && initialProperty.id) {
                    fetchDuesSettingsMutate(initialProperty.id);
                }
            }
        }
    }, [propertyList, propertyParam, fetchDuesSettingsMutate, selectedProperty]);

    useEffect(() => {
        if (settingsData) {
            setSettings({
                hideDues: settingsData?.canHideDues === 1,
                autoReminders: settingsData?.autoReminders === 1,
                allowPartialPayment: settingsData?.partialPayment === 1,
                manualReminders: settingsData?.manualReminders === 1,
                expenseAllocationByBed: settingsData?.expenseAllocationByBed === 1,
            });
        }
    }, [settingsData]);

    const toggleSetting = (key) => {
        const newValue = !settings[key];
        const updatedSettings = { ...settings, [key]: newValue };
        setSettings(updatedSettings);

        const payload = {
            propIds: [selectedPropertyId],
            canHideDues: updatedSettings.hideDues ? 1 : 0,
            partialPayment: updatedSettings.allowPartialPayment ? 1 : 0,
            autoReminders: updatedSettings.autoReminders ? 1 : 0,
            manualReminders: updatedSettings.manualReminders ? 1 : 0,
            expenseAllocationByBed: updatedSettings.expenseAllocationByBed ? 1 : 0,
            replicate: 0,
            selectedPropertyId: selectedPropertyId
        };
        editSettingsMutate(payload);
    };

    const handleSelectedProperty = (selectedOption) => {
        const newPropertyId = selectedOption.id;

        if (newPropertyId === selectedPropertyId) {
            setSelectedProperty(selectedOption);
            return;
        }

        setIsPropertyChanging(true);
        setSelectedProperty(selectedOption);
        setSelectedPropertyId(newPropertyId);

        setSettings({
            hideDues: false,
            autoReminders: false,
            allowPartialPayment: false,
            manualReminders: false,
            expenseAllocationByBed: false,
        });

        if (fetchDuesSettingsMutate) {
            fetchDuesSettingsMutate(newPropertyId, {
                onSettled: () => {
                    setIsPropertyChanging(false);
                }
            });
        } else {
            setIsPropertyChanging(false);
        }
    };

    const handleReplicateSettings = () => {
        if (selectedProperties.length === 0) {
            setShowMultiSelectModal(true);
            return;
        }

        const payload = {
            propIds: selectedProperties,
            canHideDues: settings.hideDues ? 1 : 0,
            autoReminders: settings.autoReminders ? 1 : 0,
            partialPayment: settings.allowPartialPayment ? 1 : 0,
            manualReminders: settings.manualReminders ? 1 : 0,
            expenseAllocationByBed: settings.expenseAllocationByBed ? 1 : 0,
            replicate: 1,
            selectedPropertyId: selectedPropertyId
        };
        editSettingsMutate(payload);
    };

    const handleMultiPropertySave = (selectedProps) => {
        setSelectedProperties(selectedProps);
        setShowMultiSelectModal(false);
    };

    const renderSelectedProperties = (selected = []) => {
        if (!selected.length) return "No properties selected";

        if (selected.length === allProperties.length) {
            return "All Properties";
        }

        const selectedPropertyNames = allProperties
            .filter((prop) => selected.includes(prop.id))
            .map((prop) => prop.name);

        if (!selectedPropertyNames.length) return "No properties selected";

        const maxToShow = 2;
        const namesToShow = selectedPropertyNames.slice(0, maxToShow);
        const remainingCount = selectedPropertyNames.length - maxToShow;

        return (
            <PropertyDisplayContainer>
                {namesToShow.join(", ")}
                {remainingCount > 0 && (
                    <RemainingCount>+{remainingCount}</RemainingCount>
                )}
            </PropertyDisplayContainer>
        );
    };

    const showLoading = fetchSettingsLoading || !isDataLoaded || isPropertyChanging;

    return (
        <div className="bg-white w-full h-full flex flex-col">
            <ModalHeader>
                <HeaderTitle>Finance Settings</HeaderTitle>
                <CloseButton onClick={onClose}>
                    <MdClose color="white" size={24} />
                </CloseButton>
            </ModalHeader>

            {showLoading ? (
                <div className="flex items-center justify-center h-full">
                    <CircularProgress />
                </div>
            ) : (
                <>
                    <ContentContainer>
                        <ContentWrapper>
                            <Section>
                                {/* Show property selector only when "All Properties" is selected in main dropdown */}
                                {/* {(property?.id === 0 || propertyParam === 0) && filteredProperties.length > 0 && (
                                    <PropertySection>
                                        <CategoryCard
                                            title="Property"
                                            selectedCategory={selectedProperty}
                                            onClick={() => setShowPropertyModal(true)}
                                        />
                                    </PropertySection>
                                )} */}

                                {/* <SettingsGrid>
                                    <SettingCard>
                                        <SettingContent>
                                            <SettingHeader>
                                                <SettingTitle>Hide Dues</SettingTitle>
                                                <SettingDescription>
                                                    Hide outstanding dues from tenant view. When enabled, tenants will not be able to see their pending payment amounts or overdue balances.
                                                </SettingDescription>
                                            </SettingHeader>
                                            <Toggle
                                                checked={settings.hideDues}
                                                onChange={() => toggleSetting("hideDues")}
                                                disabled={isLoading || isPropertyChanging}
                                            />
                                        </SettingContent>
                                    </SettingCard>

                                    <SettingCard>
                                        <SettingContent>
                                            <SettingHeader>
                                                <SettingTitle>Allow Partial Payments</SettingTitle>
                                                <SettingDescription>
                                                    Enable tenants to make partial payments towards their dues. This allows flexible payment options when tenants cannot pay the full amount at once.
                                                </SettingDescription>
                                            </SettingHeader>
                                            <Toggle
                                                checked={settings.allowPartialPayment}
                                                onChange={() => toggleSetting("allowPartialPayment")}
                                                disabled={isLoading || isPropertyChanging}
                                            />
                                        </SettingContent>
                                    </SettingCard>

                                    <SettingCard>
                                        <SettingContent>
                                            <SettingHeader>
                                                <SettingTitle>Automated Payment Reminders</SettingTitle>
                                                <SettingDescription>
                                                    Allow tenants to receive automated payment and due date reminders to ensure timely payments.
                                                </SettingDescription>
                                            </SettingHeader>
                                            <Toggle
                                                checked={settings.autoReminders}
                                                onChange={() => toggleSetting("autoReminders")}
                                                disabled={isLoading || isPropertyChanging}
                                            />
                                        </SettingContent>
                                    </SettingCard>

                                    <SettingCard>
                                        <SettingContent>
                                            <SettingHeader>
                                                <SettingTitle>Manual Payment Reminders</SettingTitle>
                                                <SettingDescription>
                                                    Allow client to send personalized payment reminders via WhatsApp directly to tenants.
                                                </SettingDescription>
                                            </SettingHeader>
                                            <Toggle
                                                checked={settings.manualReminders}
                                                onChange={() => toggleSetting("manualReminders")}
                                                disabled={isLoading || isPropertyChanging}
                                            />
                                        </SettingContent>
                                    </SettingCard>
                                </SettingsGrid> */}

                                <div className="mb-2">
                                    <p className="text-xs text-gray-500 uppercase tracking-wide">
                                        Payments, Expenses & Dues
                                    </p>
                                </div>

                                <SettingsGrid>

                                    {/* Hide Dues (⚠️ Risky) */}
                                    <SettingCard $variant="warning" $active={settings.hideDues}>
                                        <SettingContent>

                                            <SettingHeader>
                                                <TitleRow>
                                                    <SettingTitle>Hide Dues from Tenants</SettingTitle>

                                                    <BadgeGroup>
                                                        {settings.hideDues && (
                                                            <StatusBadge $variant="warning">Active</StatusBadge>
                                                        )}
                                                    </BadgeGroup>
                                                </TitleRow>

                                                <SettingDescription>
                                                    Tenants will not see their pending or overdue payments.
                                                </SettingDescription>

                                                <ImpactText className="text-red-500">
                                                    ↓ May delay payments and reduce transparency
                                                </ImpactText>

                                                {!settings.hideDues && (
                                                    <HelperText>
                                                        Recommended to keep visible for better payment transparency.
                                                    </HelperText>
                                                )}
                                            </SettingHeader>

                                            <ToggleWrapper>
                                                <Toggle
                                                    checked={settings.hideDues}
                                                    onChange={() => toggleSetting("hideDues")}
                                                    disabled={isLoading || isPropertyChanging}
                                                />
                                            </ToggleWrapper>

                                        </SettingContent>
                                    </SettingCard>


                                    {/* Partial Payments */}
                                    {/* <SettingCard $variant="info" $active={settings.allowPartialPayment}>
                                        <SettingContent>

                                            <SettingHeader>
                                                <TitleRow>
                                                    <SettingTitle>Allow Partial Payments</SettingTitle>

                                                    <BadgeGroup>
                                                        <RecommendedBadge>Recommended</RecommendedBadge>

                                                        {settings.allowPartialPayment && (
                                                            <StatusBadge $variant="info">Active</StatusBadge>
                                                        )}
                                                    </BadgeGroup>
                                                </TitleRow>

                                                <SettingDescription>
                                                    Let tenants pay dues in parts instead of full amount at once.
                                                </SettingDescription>

                                                <ImpactText>
                                                    ↑ Can reduce unpaid dues by 10–15%
                                                </ImpactText>

                                                {!settings.allowPartialPayment && (
                                                    <HelperText>
                                                        Improves collection chances when tenants face cash constraints.
                                                    </HelperText>
                                                )}
                                            </SettingHeader>

                                            <ToggleWrapper>
                                                <Toggle
                                                    checked={settings.allowPartialPayment}
                                                    onChange={() => toggleSetting("allowPartialPayment")}
                                                    disabled={isLoading || isPropertyChanging}
                                                />
                                            </ToggleWrapper>

                                        </SettingContent>
                                    </SettingCard> */}


                                    {/* Auto Reminders (High impact) */}
                                    {/* <SettingCard $variant="success" $active={settings.autoReminders}>
                                        <SettingContent>

                                            <SettingHeader>
                                                <TitleRow>
                                                    <SettingTitle>Automated Payment Reminders</SettingTitle>

                                                    <BadgeGroup>
                                                        <RecommendedBadge>Recommended</RecommendedBadge>

                                                        {settings.autoReminders && (
                                                            <StatusBadge $variant="success">Active</StatusBadge>
                                                        )}
                                                    </BadgeGroup>
                                                </TitleRow>

                                                <SettingDescription>
                                                    Automatically remind tenants about due payments and deadlines.
                                                </SettingDescription>

                                                <ImpactText>
                                                    ↑ Can improve on-time payments by 20–30%
                                                </ImpactText>

                                                {!settings.autoReminders && (
                                                    <HelperText>
                                                        Enabling this can significantly improve on-time collections.
                                                    </HelperText>
                                                )}
                                            </SettingHeader>

                                            <ToggleWrapper>
                                                <Toggle
                                                    checked={settings.autoReminders}
                                                    onChange={() => toggleSetting("autoReminders")}
                                                    disabled={isLoading || isPropertyChanging}
                                                />
                                            </ToggleWrapper>

                                        </SettingContent>
                                    </SettingCard> */}


                                    {/* Manual Reminders */}
                                    <SettingCard $variant="info" $active={settings.manualReminders}>
                                        <SettingContent>

                                            <SettingHeader>
                                                <TitleRow>
                                                    <SettingTitle>Manual Reminders (WhatsApp)</SettingTitle>

                                                    <BadgeGroup>
                                                        {settings.manualReminders && (
                                                            <StatusBadge $variant="info">Active</StatusBadge>
                                                        )}
                                                    </BadgeGroup>
                                                </TitleRow>

                                                <SettingDescription>
                                                    Send custom payment reminders to tenants via WhatsApp.
                                                </SettingDescription>

                                                <ImpactText>
                                                    ↑ Useful for high-value or overdue tenant follow-ups
                                                </ImpactText>

                                                {!settings.manualReminders && (
                                                    <HelperText>
                                                        Useful for personalized follow-ups with tenants.
                                                    </HelperText>
                                                )}
                                            </SettingHeader>

                                            <ToggleWrapper>
                                                <Toggle
                                                    checked={settings.manualReminders}
                                                    onChange={() => toggleSetting("manualReminders")}
                                                    disabled={isLoading || isPropertyChanging}
                                                />
                                            </ToggleWrapper>

                                        </SettingContent>
                                    </SettingCard>
                                    <SettingCard $variant="info" $active={settings.expenseAllocationByBed}>
                                        <SettingContent>

                                            <SettingHeader>
                                                <TitleRow>
                                                    <SettingTitle>Expense Allocation by Beds</SettingTitle>

                                                    <BadgeGroup>
                                                        {settings.expenseAllocationByBed && (
                                                            <StatusBadge $variant="info">Active</StatusBadge>
                                                        )}
                                                    </BadgeGroup>
                                                </TitleRow>

                                                <SettingDescription>
                                                    Allocate property expenses (e.g; utilities, repairs) based on the number of beds per property, rather than equal division.
                                                </SettingDescription>

                                                <ImpactText>
                                                    ↑ More precise and equitable distribution
                                                </ImpactText>
                                            </SettingHeader>

                                            <ToggleWrapper>
                                                <Toggle
                                                    checked={settings.expenseAllocationByBed}
                                                    onChange={() => toggleSetting("expenseAllocationByBed")}
                                                    disabled={isLoading || isPropertyChanging}
                                                />
                                            </ToggleWrapper>

                                        </SettingContent>
                                    </SettingCard>
                                </SettingsGrid>

                                {/* Replicate Settings Section - Only show when multiple properties exist */}
                                {/* {filteredProperties.length > 1 && (
                                    <ReplicateSection>
                                        <ReplicateHeader>
                                            <ReplicateTitle>Replicate Settings to Other Properties</ReplicateTitle>
                                        </ReplicateHeader>

                                        <ReplicateContent>
                                            <PropertiesSelection>
                                                <CategoryCard
                                                    type="button"
                                                    selectedCategory={selectedProperties.length > 0 ? {
                                                        label: renderSelectedProperties(selectedProperties),
                                                        value: "properties"
                                                    } : null}
                                                    title="Property or properties"
                                                    onClick={() => setShowMultiSelectModal(true)}
                                                />

                                                <NewPackage
                                                    onClick={handleReplicateSettings}
                                                    disabled={isLoading || selectedProperties.length === 0 || isPropertyChanging}
                                                >
                                                    {editSettingsLoading ? "Replicating..." : `Replicate to ${selectedProperties.length} Properties`}
                                                </NewPackage>
                                            </PropertiesSelection>
                                        </ReplicateContent>
                                    </ReplicateSection>
                                )} */}
                            </Section>
                        </ContentWrapper>

                        {/* Single Property Selection Modal */}
                        <SinglePropSelectModal
                            isOpen={showPropertyModal}
                            onClose={() => setShowPropertyModal(false)}
                            onSelect={handleSelectedProperty}
                            options={filteredProperties}
                            selectedProperty={selectedProperty}
                        />

                        {/* Multi Property Selection Modal */}
                        <MultiPropSelectModal
                            isOpen={showMultiSelectModal}
                            onClose={() => setShowMultiSelectModal(false)}
                            onSelect={handleMultiPropertySave}
                            options={filteredProperties}
                            selectedProperties={selectedProperties}
                        />
                    </ContentContainer>
                </>
            )}
        </div>
    );
};

// Styled Components
const ContentContainer = tw.div`flex-1 overflow-y-auto`;
const ContentWrapper = tw.div`p-6`;
const Section = tw.div`space-y-6`;
const Header = tw.div`flex items-center justify-between bg-[#0078BD] p-4`;
const HeaderTitle = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button``;
const PropertySection = tw.div`bg-gray-50 rounded-xl p-4 border border-gray-200 mb-4`;
const PropertyDisplayContainer = tw.div`flex items-center gap-1 truncate`;
const RemainingCount = tw.span`text-gray-500 ml-1 font-medium`;
const SettingsGrid = tw.div`space-y-4`;
// const SettingCard = tw.div`bg-gray-50 rounded-xl border border-gray-200 hover:shadow-md transition-shadow duration-200`;
// const SettingContent = tw.div`flex items-start justify-between space-x-6 p-5`;
// const SettingHeader = tw.div`flex-1`;
// const SettingTitle = tw.h3`font-semibold text-gray-800 mb-2 text-base`;
// const SettingDescription = tw.p`text-sm text-gray-600 leading-relaxed`;

const Toggle = ({ checked, onChange, disabled }) => (
    <ToggleLabel disabled={disabled}>
        <ToggleInput
            type="checkbox"
            checked={checked}
            onChange={onChange}
            disabled={disabled}
        />
        <ToggleSlider checked={checked} disabled={disabled}>
            <ToggleThumb checked={checked} />
        </ToggleSlider>
    </ToggleLabel>
);

const ToggleLabel = tw.label`${props => props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} relative inline-flex items-center`;
const ToggleInput = tw.input`sr-only peer`;
// const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.disabled ? 'bg-gray-200 cursor-not-allowed' : props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
const ToggleSlider = tw.div`relative w-12 h-6 rounded-full transition-colors duration-200 ease-in-out ${props => props.checked ? 'bg-primary-color' : 'bg-gray-300'}`;
const ToggleThumb = tw.div`absolute top-0.5 left-0.5 w-5 h-5 bg-white border border-gray-300 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${props => props.checked ? 'transform translate-x-6' : ''}`;

const SettingCard = tw.div`
  relative rounded-xl border p-4 transition-all duration-200 bg-white
  ${(p) =>
        p.$active
            ? "border-gray-300 shadow-sm"
            : "border-gray-200 hover:border-gray-300 hover:shadow-sm"}
`;

const SettingContent = tw.div`
  flex items-center justify-between gap-4
`;

const SettingHeader = tw.div`flex-1`;

const SettingTitle = tw.h3`
  text-sm font-semibold text-gray-800
`;

const SettingDescription = tw.p`
  text-xs text-gray-500 leading-relaxed
`;

const ToggleWrapper = tw.div`
  flex items-center justify-center
`;

const StatusBadge = tw.span`
  text-[10px] font-medium px-2 py-0.5 rounded-full
  ${(p) =>
        p.$variant === "warning"
            ? "bg-yellow-100 text-yellow-700"
            : "bg-green-100 text-green-700"}
`;

const HelperText = tw.p`
  text-xs text-gray-400 mt-1
`;

const TitleRow = tw.div`
  flex items-center justify-between gap-2 mb-1
`;

export default DuesSettingsModal;